import { ServerErrorBoundary } from "layouts/errors/ServerErrorBoundary";
import { type ReactNode, useContext } from "react";
import { Route } from "react-router";

import { AppProvider } from "helpers/AppProvider";
import { AppProviderHandler } from "helpers/AppProvider.api.handler";
import {
    type AppDefaultInfo,
    type BuilderInfo,
    type EnvironmentInfo,
    type UserInfo,
} from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes, type MenuItemName } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";

import { ErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import PublicEphBanner from "commonComponents/utilities/MainNavigation/common/EphInfo/PublicEphBanner";
import { MainNavigation } from "commonComponents/utilities/MainNavigation/MainNavigation";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";

import "./FullPageLayout.less";

export interface IFullPageLayoutProps {
    path?: string;
    exact?: boolean;
    element: ReactNode;
    selectedMenuItem?: MenuItemName;
    appProviderHandler?: AppProviderHandler;
    showNav?: boolean;
    /**
     * Title to be displayed in the browser tab
     */
    title: string;
}

interface IWrappedComponentProps {
    children?: ReactNode;
    showNav?: boolean;
}

function WrappedComponent({ children, showNav = true }: IWrappedComponentProps) {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const { Track } = useTracking({
        builderId: builderInfo?.builderId,
    });

    return (
        <Track>
            <ServerErrorBoundary>
                <PublicEphBanner />
                <div className="FullPageLayout">
                    {showNav && (
                        <MainNavigation
                            builderId={builderInfo?.builderId ?? 0}
                            userId={userInfo?.globalUserId ?? 0}
                            loginType={userInfo?.loginType ?? BTLoginTypes.ALL}
                            jobId={0} // No job context needed for empty layout
                            isTemplateMode={false}
                            className="FullPageLayout--nav"
                        />
                    )}

                    <ErrorBoundary>
                        <div className="FullPageLayout--body">
                            <div className="FullPageLayout--body--content">{children}</div>
                        </div>
                    </ErrorBoundary>
                </div>
            </ServerErrorBoundary>
        </Track>
    );
}

interface IFullPageLayoutInternalProps extends IFullPageLayoutProps {
    builderInfo?: BuilderInfo | null;
    userInfo?: UserInfo;
    appDefaultInfo?: AppDefaultInfo;
    envInfo?: EnvironmentInfo;
}

function FullPageLayoutInternal(props: IFullPageLayoutInternalProps) {
    return (
        <Route path={props.path} exact={props.exact}>
            <PageTitle title={props.title} />
            <WrappedComponent showNav={props.showNav}>{props.element}</WrappedComponent>
        </Route>
    );
}

const FullPageLayoutContext: React.FC<IFullPageLayoutProps> = (props) => {
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const envInfo = useContext(EnvironmentInfoContext);

    return (
        <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
            <BTLinkSpaRoutingProvider>
                <FullPageLayoutInternal
                    {...props}
                    appDefaultInfo={appDefaultInfo}
                    builderInfo={builderInfo}
                    userInfo={userInfo}
                    envInfo={envInfo}
                />
            </BTLinkSpaRoutingProvider>
        </SpaInfoContext.Provider>
    );
};

const defaultAppProviderHandler = new AppProviderHandler();
export const FullPageLayout: React.FC<IFullPageLayoutProps> = ({
    appProviderHandler = defaultAppProviderHandler,
    ...props
}) => {
    return (
        <AppProvider handler={appProviderHandler}>
            <FocusProvider shareFocusWithParent>
                <FullPageLayoutContext {...props} />
            </FocusProvider>
        </AppProvider>
    );
};
